// Centralised fetch wrapper. Attaches JWT from localStorage when present.
// All endpoints are JSON; no server-rendered HTML is ever returned.

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
const TOKEN_KEY = 'babysteps.jwt';

/**
 * Active UI/content locale, appended as ?locale=… to every request so the
 * backend localises catalog content immediately (even before /me reflects a
 * change). Set by the I18nProvider; safe to leave null (backend defaults to the
 * user's stored locale, else English).
 */
let activeLocale: string | null = null;

export function setActiveApiLocale(locale: string | null): void {
  activeLocale = locale;
}

/** Append ?locale=… unless the caller already specified one. */
function withLocale(path: string): string {
  if (!activeLocale || /[?&]locale=/.test(path)) return path;
  return path + (path.includes('?') ? '&' : '?') + 'locale=' + encodeURIComponent(activeLocale);
}

/**
 * Absolute URL for a PUBLIC catalog/editorial image
 * (GET /catalog/media/{id}/raw). No auth header is required, so it can be
 * dropped straight into an <img src> — the browser then HTTP-caches it and
 * lazy-loads it natively, instead of the authed fetch→decrypt→objectURL path
 * in useMediaBlob. Only valid for non-encrypted catalog media. `thumb`
 * requests the 400px server thumbnail used in list views.
 */
export function catalogMediaUrl(id: string, opts: { thumb?: boolean } = {}): string {
  return `${BASE_URL}/catalog/media/${id}/raw${opts.thumb ? '?w=400' : ''}`;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* no-op */
  }
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly body?: unknown) {
    super(message);
  }
}

/** The custom event the BillingProvider listens for to open the paywall. */
export const PAYWALL_EVENT = 'babysteps:paywall';

/**
 * The custom event MainApp listens for to raise the email-verification gate.
 * Fired on any 403 email_unverified so a mid-session grace-period expiry flips
 * to the blocking screen without waiting for the next /me refetch.
 */
export const EMAIL_UNVERIFIED_EVENT = 'babysteps:email-unverified';

/**
 * When the backend rejects an action with 402 quota_exceeded, broadcast it so a
 * global listener (BillingProvider) can open a targeted paywall — no per-call
 * wiring needed at the dozens of gated mutations.
 */
function maybeSignalPaywall(status: number, payload: unknown): void {
  if (status !== 402 || typeof window === 'undefined') return;
  const err = (payload as { error?: { code?: string } } | undefined)?.error;
  if (err?.code === 'quota_exceeded' || err?.code === 'subscription_required') {
    window.dispatchEvent(new CustomEvent(PAYWALL_EVENT, { detail: err }));
  }
}

/**
 * When the backend rejects a call with 403 email_unverified, broadcast it so
 * MainApp can invalidate /me and render the blocking EmailVerificationScreen.
 */
function maybeSignalEmailUnverified(status: number, payload: unknown): void {
  if (status !== 403 || typeof window === 'undefined') return;
  const err = (payload as { error?: { code?: string } } | undefined)?.error;
  if (err?.code === 'email_unverified') {
    window.dispatchEvent(new CustomEvent(EMAIL_UNVERIFIED_EVENT, { detail: err }));
  }
}

/**
 * The custom event MainApp listens for to raise the legal-acceptance gate.
 * Fired on any 403 legal_acceptance_required so a mid-session version bump
 * flips to the blocking screen without waiting for the next /me refetch.
 */
export const LEGAL_ACCEPTANCE_REQUIRED_EVENT = 'babysteps:legal-acceptance-required';

function maybeSignalLegalAcceptanceRequired(status: number, payload: unknown): void {
  if (status !== 403 || typeof window === 'undefined') return;
  const err = (payload as { error?: { code?: string } } | undefined)?.error;
  if (err?.code === 'legal_acceptance_required') {
    window.dispatchEvent(new CustomEvent(LEGAL_ACCEPTANCE_REQUIRED_EVENT, { detail: err }));
  }
}

/**
 * The custom event MainApp listens for to raise the account-deletion gate.
 * Fired on any 403 account_deletion_pending — e.g. deletion was requested
 * from another device/session — so this tab flips to the blocking screen
 * without waiting for the next /me refetch.
 */
export const ACCOUNT_DELETION_PENDING_EVENT = 'babysteps:account-deletion-pending';

function maybeSignalAccountDeletionPending(status: number, payload: unknown): void {
  if (status !== 403 || typeof window === 'undefined') return;
  const err = (payload as { error?: { code?: string } } | undefined)?.error;
  if (err?.code === 'account_deletion_pending') {
    window.dispatchEvent(new CustomEvent(ACCOUNT_DELETION_PENDING_EVENT, { detail: err }));
  }
}

/**
 * The custom event AuthProvider listens for to clear sessions on expired/invalid JWTs.
 * Broadcast on any 401 response (except /auth/* routes).
 */
export const AUTH_EXPIRED_EVENT = 'babysteps:auth-expired';

function maybeSignalAuthExpired(status: number, path: string): void {
  if (status !== 401 || typeof window === 'undefined') return;
  if (path.startsWith('/auth/verify') || path.startsWith('/auth/login')) return;
  setToken(null);
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}

/**
 * The custom event a global toast listens for to show a transient "slow down"
 * notice, fired on any 429 rate_limited response (the per-endpoint throttles
 * and the overall per-user cap both use this status/code).
 */
export const RATE_LIMITED_EVENT = 'babysteps:rate-limited';

export interface RateLimitedDetail {
  /** Seconds until the client may retry, from the Retry-After header. */
  retryAfter: number | null;
}

function maybeSignalRateLimited(status: number, res: Response): void {
  if (status !== 429 || typeof window === 'undefined') return;
  const header = res.headers.get('Retry-After');
  const retryAfter = header !== null && header !== '' ? Number(header) : null;
  const detail: RateLimitedDetail = { retryAfter: Number.isFinite(retryAfter) ? retryAfter : null };
  window.dispatchEvent(new CustomEvent(RATE_LIMITED_EVENT, { detail }));
}

type RequestOpts = Omit<RequestInit, 'body'> & { body?: unknown };

export async function api<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  headers.set('Accept', 'application/json');
  // FormData bodies set their own multipart Content-Type (with boundary), so we
  // must NOT override it and must not JSON-stringify them.
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  if (opts.body !== undefined && !isForm) headers.set('Content-Type', 'application/json');
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${BASE_URL}${withLocale(path)}`, {
    ...opts,
    headers,
    body:
      opts.body === undefined
        ? undefined
        : isForm
        ? (opts.body as FormData)
        : JSON.stringify(opts.body),
  });

  if (!res.ok) {
    let payload: unknown = undefined;
    try {
      payload = await res.json();
    } catch {
      /* ignore */
    }
    maybeSignalAuthExpired(res.status, path);
    maybeSignalPaywall(res.status, payload);
    maybeSignalEmailUnverified(res.status, payload);
    maybeSignalLegalAcceptanceRequired(res.status, payload);
    maybeSignalAccountDeletionPending(res.status, payload);
    maybeSignalRateLimited(res.status, res);
    const backendMessage = (payload as { error?: { message?: string } } | undefined)?.error?.message;
    throw new ApiError(res.status, backendMessage || `API ${res.status}`, payload);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Fetch raw binary content (e.g. GET /media/{id}/raw) with the auth header.
 * Returns the bytes as a Uint8Array; used for client-side decryption + display.
 */
export async function apiBytes(path: string): Promise<Uint8Array> {
  const headers = new Headers();
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${BASE_URL}${withLocale(path)}`, { headers });
  if (!res.ok) {
    maybeSignalAuthExpired(res.status, path);
    maybeSignalRateLimited(res.status, res);
    throw new ApiError(res.status, `Failed to fetch raw media: ${res.status}`);
  }
  return new Uint8Array(await res.arrayBuffer());
}
